<template>
  <div>
    <collapse v-if="users && users.length > 0">
      <collapse-item 
        v-for="(user, index) in users" 
        :key="user.id" 
        :title="`${user.name} - ${user.role.name}`" 
        @opening="openUser(parseInt(user.id))"
      >
        <div v-if="sortedUserPermissions[user.id]" class="row">
          <div 
            class="col-md-6" 
            v-for="permission in sortedUserPermissions[user.id]" 
            :key="permission.controller"
          >
            <card>
              <h5 class="h3 mb-0">{{ permission.name }}</h5>
              <div class="grid-container">
                <div 
                  class="grid-item" 
                  v-for="action in permission.actions" 
                  :key="action.action"
                >
                  <div class="grid-column">
                    <span class="font-weight-bold">{{ action.name }}:</span>
                  </div>
                  <div class="grid-column ">
                    <div class="text-success custom-center" v-if="action.allowed">
                    <span class="ni ni-check-bold"></span>
                    <span>Permitido</span>
                  </div>
                    <div class="text-danger custom-center" v-else>
                      <span class="ni ni-fat-remove icon-spacing"></span>
                      <span>Não permitido</span>
                  </div>
                  </div>
                </div>
              </div>
            </card>
          </div>
        </div>
        <div v-else>
          <p>Carregando permissões...</p>
        </div>
      </collapse-item>
    </collapse>
    <collapse v-if="(roles && roles.length > 0) && !users.length">
      <collapse-item 
        v-for="(role, index) in roles" 
        :key="role.id" 
        :title="role.name" 
        @opening="openRole(role.id)"
      >
        <div v-if="sortedRolePermissions[role.id]" class="row">
          <div 
            class="col-md-6" 
            v-for="permission in sortedRolePermissions[role.id]" 
            :key="permission.controller"
          >
            <card>
              <h5 class="h3 mb-0">{{ permission.name }}</h5>
              <div class="grid-container">
                <div 
                  class="grid-item" 
                  v-for="action in permission.actions" 
                  :key="action.action"
                >
                  <span class="font-weight-bold">{{ action.name }}:</span>
                  <div class="text-success custom-center" v-if="action.allowed">
                    <span class="ni ni-check-bold"></span>
                    <span>Permitido</span>
                  </div>
                  <div class="text-danger custom-center" v-else>
                    <span class="ni ni-fat-remove icon-spacing"></span>
                    <span>Não permitido</span>
                  </div>
                </div>
              </div>
            </card>
          </div>
        </div>
      </collapse-item>
    </collapse>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed, defineEmits } from 'vue';
import axios from "@/backend/axios/index.js";
import Collapse from '@/components/Collapse/Collapse';
import CollapseItem from '@/components/Collapse/CollapseItem';

const props = defineProps({
  userIds: Array,
  roleIds: Array,
  permissionIds: Array,
});
const emit = defineEmits();
const users = ref([]);
const roles = ref([]);
const userPermissions = ref({});
const rolePermissions = ref({});
const controllerPermissions = ref([]);

const filteredUserPermissions = (user_id) => {
  const permissions = userPermissions.value[user_id] || [];

  if (!props.permissionIds || props.permissionIds.length === 0) {
    return permissions.sort((a, b) => a.name.localeCompare(b.name));
  }

  return permissions
    .filter(permission => props.permissionIds.includes(permission.name))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const filteredRolePermissions = (role_id) => {
  const permissions = rolePermissions.value[role_id] || [];

  if (!props.permissionIds || props.permissionIds.length === 0) {
    return permissions.sort((a, b) => a.name.localeCompare(b.name));
  }

  return permissions
    .filter(permission => props.permissionIds.includes(permission.name))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const loading = computed({
  get: () => props.loading,
  set: (value) => emit("update:loading", value),
});

const sortedUserPermissions = computed(() => {
  const sorted = {};
  Object.keys(userPermissions.value).forEach(user_id => {
    sorted[user_id] = filteredUserPermissions(user_id).map(permission => ({
      ...permission,
      actions: [...permission.actions].sort((a, b) => a.name.localeCompare(b.name))
    }));
  });
  return sorted;
});

const sortedRolePermissions = computed(() => {
  const sorted = {};
  Object.keys(rolePermissions.value).forEach(role_id => {
    sorted[role_id] = filteredRolePermissions(role_id).map(permission => ({
      ...permission,
      actions: [...permission.actions].sort((a, b) => a.name.localeCompare(b.name))
    }));
  });
  return sorted;
});

const getUsers = async () => {
  loading.value = true;
  if (props.userIds.length > 0) {
    try {
      const response = await axios.get("/api/v1/users", {
        params: { user_ids: props.userIds }
      });
      users.value = response.data || [];
      console.log('Usuários carregados:', users.value);
    } catch (error) {
      console.error("[PermissionTable][getUsers]", error);
    } finally {
      loading.value = false;
    }
  } else {
    users.value = []; 
  }
};

const getRoles = async () => {
  loading.value = true;
  if (props.roleIds.length > 0) {
    try {
      const response = await axios.get("/api/v1/roles", {
        params: { role_ids: props.roleIds }
      });
      roles.value = response.data || [];
      console.log('Grupos carregados:', roles.value);
    } catch (error) {
      console.error("[PermissionTable][getRoles]", error);
    } finally {
      loading.value = false;
    }
  } else {
    roles.value = []; 
  }
};

watch(() => props.userIds, () => {
  getUsers();
});

watch(() => props.roleIds, () => {
  getRoles();
});

const getControllerPermissions = async () => {
  const currentUser = JSON.parse(localStorage.currentUser);
  const path = `/api/v1/users/${currentUser.id}/permissions`;
  try {
    const response = await axios.get(path, { params: { permissionable_controllers: true } });
    controllerPermissions.value = response.data;
  } catch (error) {
    console.error("[Error][getControllerPermissions]", error);
  }
};
const getUserPermissions = async (user_id) => {
  try {
    const response = await axios.get(`/api/v1/users/${user_id}/permissions`, {
      params: { user_permissions: true }
    });

    const userPermissionsData = response.data;
    const userPermissionsByAction = {};

    userPermissionsData.forEach((permission) => {
      permission.actions.forEach(action => {
        if (!userPermissionsByAction[permission.controller]) {
          userPermissionsByAction[permission.controller] = {};
        }
        userPermissionsByAction[permission.controller][action.action] = true;
      });
    });

    const mappedUserPermissions = controllerPermissions.value.map((permission) => {
      const hasManagePermission = userPermissionsData.some((perm) =>
        perm.controller === permission.controller && perm.actions.some((action) => action.action === 'manage')
      );

      const hasTotalPermission = userPermissionsData.some((perm) => 
        perm.controller === 'total'
      );

      const actions = permission.actions.map((action) => ({
        ...action,
        allowed: hasTotalPermission || hasManagePermission || 
                 (userPermissionsByAction[permission.controller] && userPermissionsByAction[permission.controller][action.action])
      }));

      return {  
        ...permission,
        actions
      };
    });

    userPermissions.value = Object.assign({}, userPermissions.value, { [user_id]: mappedUserPermissions });
    console.log('user permission', userPermissions.value);
  } catch (error) {
    console.error('Erro ao buscar permissões dos usuários:', error);
  }
};

const getRolePermissions = async (role_id) => {
  try {
    const response = await axios.get(`/api/v1/roles/${role_id}/permissions`, {
      params: { role_permissions: true }
    });

    const rolePermissionsData = response.data;
    const rolePermissionsByAction = {};

    rolePermissionsData.forEach((permission) => {
      permission.actions.forEach(action => {
        if (!rolePermissionsByAction[permission.controller]) {
          rolePermissionsByAction[permission.controller] = {};
        }
        rolePermissionsByAction[permission.controller][action.action] = true;
      });
    });

    const mappedRolePermissions = controllerPermissions.value.map((permission) => {
      const hasManagePermission = rolePermissionsData.some((perm) =>
        perm.controller === permission.controller && perm.actions.some((action) => action.action === 'manage')
      );
      const hasTotalPermission = rolePermissionsData.some((perm) => 
        perm.controller === 'total'
      );
      const actions = permission.actions.map((action) => ({
        ...action,
        allowed: hasTotalPermission || hasManagePermission || ( rolePermissionsByAction[permission.controller] ? rolePermissionsByAction[permission.controller][action.action] : false)
      }));

      return {  
        ...permission,
        actions
      };
    });

    rolePermissions.value = Object.assign({}, rolePermissions.value, { [role_id]: mappedRolePermissions });
    console.log('role permission', rolePermissions.value);
  } catch (error) {
    console.error('Erro ao buscar permissões dos grupos:', error);
  }
};

const openUser = async (user_id) => {
  if (!userPermissions.value[user_id]) {
    await getUserPermissions(user_id);
  }
};
const openRole = (role_id) => {
  if (!rolePermissions.value[role_id]) {
    getRolePermissions(role_id);
  }
}

onMounted(() => {
  getControllerPermissions();
});
</script>

<style>
.custom-center {
    display: flex;
    align-items: center; 
    justify-content: start;
    gap: 0.2rem; 
}
.grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 10px;
}
.grid-item {
  display: contents;
}
.grid-column {
  display: flex;
  align-items: center;
}
.accordion {
  padding-bottom: 10px;
}
</style>